import { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import toast from 'react-hot-toast';
import API_BASE_URL from '../api/Config';
import "./Wishlist.css";

// ─── Icons ───────────────────────────────────────────────
const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
  </svg>
);

const IconArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const IconShoppingCart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);

// ─── Component ────────────────────────────────────────────
export default function Wishlist() {
  const navigate = useNavigate();
  const { wishlistItems, removeFromWishlist, getWishlistCount, loading } = useWishlist();
  const { addToCart } = useCart();
  const [removingId, setRemovingId] = useState(null);

  const totalItems = getWishlistCount();
  const isEmpty = wishlistItems.length === 0;

  const handleAddToCart = async (product) => {
    try {
      // Extract numeric price from formatted price string if needed
      let numericPrice = product.price;
      if (typeof product.price === 'string') {
        const cleaned = product.price.replace(/[KSh$\s,]/g, '');
        numericPrice = parseFloat(cleaned) || 0;
      }
      
      await addToCart({ ...product, price: numericPrice });
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      toast.error('Failed to add item to cart. Please try again.');
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      setRemovingId(productId);
      await removeFromWishlist(productId);
    } catch (error) {
      console.error('Failed to remove item from wishlist:', error);
      toast.error('Failed to remove item. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleClearWishlist = async () => {
    if (wishlistItems.length === 0) {
      toast('Your wishlist is already empty', {
        icon: 'ℹ️'
      });
      return;
    }
    
    try {
      // Clear items one by one
      let successCount = 0;
      let errorCount = 0;
      
      for (const item of wishlistItems) {
        try {
          const result = await removeFromWishlist(item.id);
          if (result) successCount++;
        } catch (error) {
          console.error(`Failed to remove item ${item.id}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Cleared ${successCount} ${successCount === 1 ? 'item' : 'items'} from wishlist`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to remove ${errorCount} ${errorCount === 1 ? 'item' : 'items'}`);
      }
    } catch (error) {
      console.error('Failed to clear wishlist:', error);
      toast.error('Failed to clear wishlist. Please try again.');
    }
  };

  const getPrice = (price) => {
    if (typeof price === 'string') {
      // Remove currency symbols and parse as float
      const cleaned = price.replace(/[KSh$\s,]/g, '');
      const numeric = parseFloat(cleaned);
      return isNaN(numeric) ? 0 : numeric;
    }
    return typeof price === 'number' ? price : 0;
  };

  const formatPrice = (price) => {
    const numeric = getPrice(price);
    return numeric.toFixed(0);
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getImageUrl = (imagePath) => {
    // Handle undefined or null
    if (!imagePath) {
      return 'https://placehold.co/400x300?text=Product+Image';
    }
    
    // Handle array - take first element
    if (Array.isArray(imagePath)) {
      imagePath = imagePath.length > 0 ? imagePath[0] : '';
      if (!imagePath) {
        return 'https://placehold.co/400x300?text=Product+Image';
      }
    }
    
    // Ensure it's a string
    imagePath = String(imagePath);
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // If it's a relative path starting with /, prepend the base URL
    if (imagePath.startsWith('/')) {
      const baseUrl = API_BASE_URL.replace('/api/v1', '');
      const fullUrl = `${baseUrl}${imagePath}`;
      return fullUrl;
    }
    
    // If it's just a filename (no leading /), it's likely in /uploads/ directory
    const baseUrl = API_BASE_URL.replace('/api/v1', '');
    const fullUrl = `${baseUrl}/uploads/${imagePath}`;
    return fullUrl;
  };

  return (
    <div className="wishlist-page">

      {/* ── Main ── */}
      <main className="wishlist-main">
        {loading ? (
          <div className="wishlist-loading">
            <div className="loading-spinner"></div>
            <p>Loading your wishlist...</p>
          </div>
        ) : (
          <>
            <div className="wishlist-title-row">
              <h1 className="wishlist-title">Your Wishlist</h1>
              {!isEmpty && (
                <span className="wishlist-count-badge">
                  {totalItems} {totalItems === 1 ? "item" : "items"}
                </span>
              )}
            </div>

        {/* ── Empty State ── */}
        {isEmpty ? (
          <div className="wishlist-empty">
            <div className="wishlist-empty-icon">
              <IconHeart />
            </div>
            <h2>Your wishlist is empty</h2>
            <p>
              Save items you love to your wishlist. Review them anytime and easily move them to the cart.
            </p>
            <button className="btn-continue-lg" onClick={() => navigate("/shop")}>
              <IconArrowLeft />
              Start Shopping
            </button>
          </div>
        ) : (
          /* ── Wishlist Grid ── */
          <div className="wishlist-grid">
            {/* ── Items ── */}
            <section className="wishlist-items-section">
              <div className="wishlist-items-header">
                <div>Product</div>
                <div style={{ textAlign: "right" }}>Price</div>
                <div style={{ textAlign: "center" }}>Actions</div>
              </div>

              <div className="wishlist-items-list">
                {wishlistItems.map((item) => {
                  
                  return (
                  <div className="wishlist-item" key={item.id}>
                    {/* Info */}
                    <div className="wishlist-item-info">
                      <div className="wishlist-item-img-placeholder">
                        <img 
                          src={getImageUrl(item.image)} 
                          alt={item.name} 
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                          onError={(e) => {
                            e.target.src = 'https://placehold.co/400x300?text=Product+Image';
                          }}
                        />
                      </div>
                      <div className="wishlist-item-details">
                        <div className="wishlist-item-name">{item.name}</div>
                        <div className="wishlist-item-variant">
                          {(() => {
                            const plainText = stripHtml(item.description || '');
                            return plainText.length > 50 ? plainText.substring(0, 50) + '...' : plainText;
                          })()}
                        </div>
                        <button
                          className="wishlist-item-remove"
                          onClick={() => handleRemoveFromWishlist(item.id)}
                          disabled={removingId === item.id}
                        >
                          <IconTrash /> {removingId === item.id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="wishlist-item-price">
                      Ksh.{formatPrice(item.price)}
                    </div>

                    {/* Actions */}
                    <div className="wishlist-item-actions">
                      <button 
                        className="btn-add-cart"
                        onClick={() => handleAddToCart(item)}
                      >
                        <IconShoppingCart /> Add to Cart
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Clear Wishlist Button */}
              <div className="wishlist-actions-footer">
                <button 
                  className="btn-clear-wishlist"
                  onClick={handleClearWishlist}
                  disabled={loading}
                >
                  <IconTrash /> {loading ? 'Clearing...' : 'Clear Wishlist'}
                </button>
              </div>
            </section>

            {/* ── Summary ── */}
            <aside className="wishlist-summary">
              <h2 className="wishlist-summary-title">Wishlist Summary</h2>

              <div className="summary-info">
                <p>You have <strong>{totalItems}</strong> {totalItems === 1 ? 'item' : 'items'} in your wishlist.</p>
                <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--ink-muted)', lineHeight: '1.6' }}>
                  Items in your wishlist are saved for later. You can move them to your cart when you're ready to purchase.
                </p>
              </div>

              <hr className="summary-divider" />

              {/* Shop Now */}
              <button className="btn-shop-now" onClick={() => navigate("/shop")}>
                Continue Shopping <IconArrowRight />
              </button>

              {/* Continue Shopping */}
              <button className="btn-view-cart" onClick={() => navigate("/cart")}>
                <IconShoppingCart /> View Cart
              </button>
            </aside>
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
}
